// src/services/dashboardService.js
import api from '../api/apiClient';

const dashboardService = {
  /**
   * Get Workshop 4 dashboard data
   * @param {string} analysisId - The analysis ID
   * @returns {Promise} API response with dashboard data
   */
  async getWorkshop4DashboardData(analysisId) {
    try {
      console.log('📊 Fetching Workshop 4 dashboard data for analysis:', analysisId);

      const response = await api.get(`/analyses/${analysisId}/dashboard/workshop4`);

      if (response.success) {
        console.log('✅ Successfully fetched Workshop 4 dashboard data:', {
          overallRiskScore: response.data.overallRiskScore,
          riskMatrixCount: response.data.riskMatrix?.length || 0,
          topAssetsCount: response.data.topAtRiskAssets?.length || 0,
          topTechniquesCount: response.data.topAttackTechniques?.length || 0,
          totalScenarios: response.data.scenarioStats?.total || 0,
          totalAttackPaths: response.data.attackPathStats?.total || 0
        });

        return {
          success: true,
          data: response.data,
          message: response.message
        };
      } else {
        console.warn('⚠️ Failed to fetch Workshop 4 dashboard data:', response.message);
        return {
          success: false,
          data: null,
          message: response.message || 'Failed to fetch dashboard data'
        };
      }
    } catch (error) {
      console.error('❌ Error fetching Workshop 4 dashboard data:', error);
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || error.message || 'Network error'
      };
    }
  },

  /**
   * Get complete dashboard data for all workshops
   * @param {string} analysisId - The analysis ID
   * @returns {Promise} API response with complete dashboard data
   */
  async getCompleteDashboardData(analysisId) {
    try {
      console.log('📊 Fetching complete dashboard data for analysis:', analysisId);

      const response = await api.get(`/analyses/${analysisId}/dashboard/complete`);

      if (response.success) {
        console.log('✅ Successfully fetched complete dashboard data');

        return {
          success: true,
          data: response.data,
          message: response.message
        };
      } else {
        console.warn('⚠️ Failed to fetch complete dashboard data:', response.message);
        return {
          success: false,
          data: null,
          message: response.message || 'Failed to fetch complete dashboard data'
        };
      }
    } catch (error) {
      console.error('❌ Error fetching complete dashboard data:', error);
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || error.message || 'Network error'
      };
    }
  },

  /**
   * Format Workshop 4 dashboard data for charts
   * @param {Object} rawData - Raw dashboard data from API
   * @returns {Object} Formatted data for dashboard charts
   */
  formatWorkshop4Data(rawData) {
    if (!rawData) {
      return {
        overallRiskScore: 0,
        riskMatrix: [],
        topAtRiskAssets: [],
        topAttackTechniques: [],
        scenarioStats: {
          total: 0,
          bySeverity: {},
          byLikelihood: {},
          bySkillLevel: {}
        },
        attackPathStats: {
          total: 0,
          byComplexity: {},
          byRiskLevel: {}
        }
      };
    }

    return {
      overallRiskScore: rawData.overallRiskScore || 0,
      riskMatrix: rawData.riskMatrix || [],
      topAtRiskAssets: rawData.topAtRiskAssets || [],
      topAttackTechniques: rawData.topAttackTechniques || [],
      scenarioStats: {
        total: rawData.scenarioStats?.total || 0,
        bySeverity: rawData.scenarioStats?.bySeverity || {},
        byLikelihood: rawData.scenarioStats?.byLikelihood || {},
        bySkillLevel: rawData.scenarioStats?.bySkillLevel || {}
      },
      attackPathStats: {
        total: rawData.attackPathStats?.total || 0,
        byComplexity: rawData.attackPathStats?.byComplexity || {},
        byRiskLevel: rawData.attackPathStats?.byRiskLevel || {}
      }
    };
  },

  /**
   * Get risk level color based on score
   * @param {number} riskScore - Risk score (1-25)
   * @returns {string} Color class or hex color
   */
  getRiskLevelColor(riskScore) {
    if (riskScore >= 16) return '#DC2626'; // Critical - Red
    if (riskScore >= 12) return '#F59E0B'; // High - Orange
    if (riskScore >= 6) return '#FBBF24';  // Medium - Yellow
    return '#10B981'; // Low - Green
  },

  /**
   * Get risk level label based on score
   * @param {number} riskScore - Risk score (1-25)
   * @returns {string} Risk level label
   */
  getRiskLevelLabel(riskScore) {
    if (riskScore >= 16) return 'Critique';
    if (riskScore >= 12) return 'Élevé';
    if (riskScore >= 6) return 'Moyen';
    return 'Faible';
  },

  /**
   * Calculate completion percentage for a workshop
   * @param {Object} workshopData - Workshop data object
   * @returns {number} Completion percentage (0-100)
   */
  calculateWorkshopCompletion(workshopData) {
    if (!workshopData) return 0;

    const keys = Object.keys(workshopData);
    const completedKeys = keys.filter(key => {
      const value = workshopData[key];
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'object' && value !== null) return Object.keys(value).length > 0;
      return value !== null && value !== undefined;
    });

    return Math.round((completedKeys.length / keys.length) * 100);
  },

  /**
   * Get Workshop 5 dashboard data
   * @param {string} analysisId - The analysis ID
   * @returns {Promise} API response with Workshop 5 dashboard data
   */
  async getWorkshop5DashboardData(analysisId) {
    try {
      console.log('📊 Fetching Workshop 5 dashboard data for analysis:', analysisId);

      const response = await api.get(`/analyses/${analysisId}/dashboard/workshop5`);

      if (response.success) {
        console.log('✅ Successfully fetched Workshop 5 dashboard data:', {
          totalScenarios: response.data.strategicScenariosStats?.total || 0,
          withCustomMeasures: response.data.strategicScenariosStats?.withCustomMeasures || 0,
          totalCustomMeasures: response.data.customMeasuresStats?.total || 0,
          averageResidualRisk: response.data.residualRiskAnalysis?.averageResidualRisk || 0
        });

        return {
          success: true,
          data: response.data,
          message: response.message
        };
      } else {
        console.warn('⚠️ Failed to fetch Workshop 5 dashboard data:', response.message);
        return {
          success: false,
          data: null,
          message: response.message || 'Failed to fetch Workshop 5 dashboard data'
        };
      }
    } catch (error) {
      console.error('❌ Error fetching Workshop 5 dashboard data:', error);
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || error.message || 'Network error'
      };
    }
  }
};

export default dashboardService;